import { Connection, Keypair } from "@solana/web3.js";
import { useAnchorWallet, useWallet } from "@solana/wallet-adapter-react";
import {
  createInitGameInstruction,
  createInitPlayerInstruction,
  createPlayInstruction,
  createSettleInstruction,
  createSetPayoutMapInstruction,
  createWithdrawFromRewardPoolInstruction,
  createWithdrawIncomeInstruction,
  PayoutConfig,
  RTD_PROGRAM_ID,
  createInitGameIncomeAccountInstruction,
  createInitGameRewardAccountInstruction,
} from "../utils/instructions";
import { Dice5 as Dice } from 'lucide-react';

import { sendTx } from "../utils/sendTx";
import { useProgram } from "../hooks/useProgram";
import * as anchor from "@coral-xyz/anchor";
import * as sb from "@switchboard-xyz/on-demand";
import { loadSbProgram, setupQueue } from "../utils/switchboardUtils";
import { AnchorProvider } from "@coral-xyz/anchor";
import React, { useEffect, useState } from "react";
import { ConnectionProvider, WalletProvider } from '@solana/wallet-adapter-react';
import { WalletModalProvider, WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import { PublicKey } from "@solana/web3.js";

import { CLUSTER_URL, ADMIN_PUBKEY, ROLL_MINT, MONOPOLY_MINT, MONOPOLY_DECIMALS, ROLL_DECIMALS } from "../../solana-constants";
import { deriveGameStatePda, derivePlayerStatePda, findGameStateAuthorityPda, findRewardPoolPda, findRollIncomePda } from "../utils/derive";
import { createTransferCheckedInstruction, getAssociatedTokenAddressSync } from "@solana/spl-token";
import RollTheDiceGame from "./RtdGame";
import { ParsedGameState, parseGameStateData } from "../RewardMap";
import GameAlert from "./GameAlert";
import PayoutMapEditor from "./PayoutMapEditor";
import { scaleDown, scaleUp } from "../utils/scale";
import WelcomeScreen from "./WelcomeScreen";

require('@solana/wallet-adapter-react-ui/styles.css');

/** 
 * A single page that:
 *  - Shows your hero/landing UI
 *  - Provides "Roll The Dice" sections
 *  - Provides Admin controls if the connected wallet matches ADMIN_PUBKEY
 */
interface RtdGamePageProps {
  gameState: PublicKey;
  playerState: PublicKey;
  rollIncome: PublicKey;
  rewardPool: PublicKey;
  gameStateAuthority: PublicKey;
  userRollTokenAccount: PublicKey;
  userMonopolyTokenAccount: PublicKey;
}

export const RtdGamePageContent: React.FC<RtdGamePageProps> = (props) => {
  const {
    gameState: gameStatePda,
    playerState,
    rollIncome,
    rewardPool,
    gameStateAuthority,
    userRollTokenAccount,
    userMonopolyTokenAccount,
  } = props;

  console.log("gameStatePda", gameStatePda.toString())
  // Wallet adapter
  const { publicKey, signTransaction } = useWallet();
  const { connection } = useProgram();
  const wallet = useAnchorWallet();
  const anchorProvider = new AnchorProvider(connection, wallet, {});

  // Switchboard program
  const [sbProgram, setSbProgram] = useState<anchor.Program | null>(null);
  const [queuePubkey, setQueuePubkey] = useState<PublicKey | null>(null);

  // Local states for randomness
  const [randomnessKp, setRandomnessKp] = useState<Keypair | null>(null);
  const [randomnessPubkey, setRandomnessPubkey] = useState<string>("");

  const [initialPayoutMap, setInitialPayoutMap] = useState<PayoutConfig[] | null>(null);
  const [gameState, setGameState] = useState<ParsedGameState | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // For withdraw amounts
  const [withdrawAmount, setWithdrawAmount] = useState<string>("0");

  // Check if connected wallet is admin
  const isAdmin = publicKey?.toBase58() === ADMIN_PUBKEY;

  // -------------------------------------------
  // 1. States for token balances
  // -------------------------------------------
  const [rewardPoolBalance, setRewardPoolBalance] = useState<string>("0");
  const [rollIncomeBalance, setRollIncomeBalance] = useState<string>("0");

  // The connected wallet's token balances
  const [userRollBalance, setUserRollBalance] = useState<string>("0");
  const [userMonopolyBalance, setUserMonopolyBalance] = useState<string>("0");

  // -------------------------------------------
  // 2. States for deposit/withdraw
  //    We'll store them as UN-SCALED decimal strings (e.g. "1.25")
  // -------------------------------------------
  const [depositAmountMonopoly, setDepositAmountMonopoly] = useState<string>("0");
  const [withdrawAmountRoll, setWithdrawAmountRoll] = useState<string>("0");
  const [withdrawAmountMonopoly, setWithdrawAmountMonopoly] = useState<string>("0");

  const [transactionStep, setTransactionStep] = useState<'play' | 'settle' | null>(null);

  useEffect(() => {
    if (gameState?.payoutMap) {
      const formattedPayoutMap = gameState.payoutMap.map(payout => ({
        dice_1: payout.dice1,
        dice_2: payout.dice2,
        payout_type: payout.payoutType,
        payout_value: BigInt(payout.payoutValue.toString())
      }));
      setInitialPayoutMap(formattedPayoutMap);
    }
  }, [gameState]);

  /**
   * Load Switchboard On-Demand & set up a queue
   */
  useEffect(() => {
    (async () => {
      if (!anchorProvider || sbProgram) {
        return;
      }

      // 1) Load Switchboard On-Demand program
      const loadedSbProgram = await loadSbProgram(anchorProvider);
      setSbProgram(loadedSbProgram);

      // 2) Setup the default queue
      const defaultQueuePubkey = await setupQueue(loadedSbProgram);
      setQueuePubkey(defaultQueuePubkey);
    })();
  }, [anchorProvider]);

  const fetchAllBalances = React.useCallback(async () => {
    if (!connection) return;

    // Reward Pool (MONOPOLY)
    if (rewardPool) {
      const balanceInfo = await connection.getTokenAccountBalance(rewardPool);
      // "amount" is a string in raw integer form
      // scaleDown for user-friendly display
      setRewardPoolBalance(scaleDown(balanceInfo?.value.amount ?? "0", MONOPOLY_DECIMALS));
    }

    // Roll Income (ROLL)
    if (rollIncome) {
      const balanceInfo2 = await connection.getTokenAccountBalance(rollIncome);
      setRollIncomeBalance(scaleDown(balanceInfo2?.value.amount ?? "0", ROLL_DECIMALS));
    }

    // User's ROLL balance
    if (userRollTokenAccount) {
      const userRollInfo = await connection.getTokenAccountBalance(userRollTokenAccount);
      setUserRollBalance(parseFloat(scaleDown(userRollInfo?.value.amount ?? "0", ROLL_DECIMALS)).toFixed(2));
    }

    // User's MONOPOLY balance
    if (userMonopolyTokenAccount) {
      const userMonoInfo = await connection.getTokenAccountBalance(userMonopolyTokenAccount);
      setUserMonopolyBalance(scaleDown(userMonoInfo?.value.amount ?? "0", MONOPOLY_DECIMALS));
    }
  }, [connection, rewardPool, rollIncome, userRollTokenAccount, userMonopolyTokenAccount]);

  // Fire on mount and when PDAs change
  useEffect(() => {
    fetchAllBalances();
  }, [fetchAllBalances]);

  // ------------------------- SWITCHBOARD ACTIONS -------------------------
  const handleInitPlayerAndRandomness = async () => {
    if (!publicKey || !signTransaction) {
      return alert("Wallet not connected");
    }
    if (!sbProgram || !queuePubkey) {
      return alert("Switchboard program or queue not loaded");
    }
    try {
      const rngKp = Keypair.generate();
      const [randomnessAccount, createIx] = await sb.Randomness.create(
        sbProgram,
        rngKp,
        queuePubkey
      );

      const createPlayerIx = createInitPlayerInstruction(publicKey, playerState);

      await sendTx(connection, { publicKey, signTransaction }, [createIx, createPlayerIx], [rngKp]);

      setRandomnessKp(rngKp);
      setRandomnessPubkey(rngKp.publicKey.toBase58());

      return rngKp
    } catch (err: any) {
      console.error(err);
      alert("Error: " + err.message);
    }

    return null
  };

  const handleInitRandomness = async () => {
    if (!publicKey || !signTransaction) {
      return alert("Wallet not connected");
    }
    if (!sbProgram || !queuePubkey) {
      return alert("Switchboard program or queue not loaded");
    }
    try {
      const rngKp = Keypair.generate();
      const [randomnessAccount, createIx] = await sb.Randomness.create(
        sbProgram,
        rngKp,
        queuePubkey
      );

      await sendTx(connection, { publicKey, signTransaction }, [createIx], [rngKp]);

      setRandomnessKp(rngKp);
      setRandomnessPubkey(rngKp.publicKey.toBase58());

      return rngKp
    } catch (err: any) {
      console.error(err);
      alert("Error: " + err.message);
    }

    return null
  };

  const handlePlay = async (): Promise<boolean> => {
    if (!publicKey || !signTransaction || !sbProgram || !queuePubkey || !randomnessPubkey || !randomnessKp) {
      alert("Missing SB program or RNG pubkey or RNG keypair");
      return false
    }

    setTransactionStep('play');

    let success = true

    const rngPubkey = new PublicKey(randomnessPubkey);
    console.log('queuePubkey', queuePubkey.toString())

    const doPlay = async () => {
      const commitIx = await new sb.Randomness(sbProgram, rngPubkey).commitIx(queuePubkey);

      const playIx = createPlayInstruction(
        playerState,
        publicKey,
        userRollTokenAccount,
        rollIncome,
        rngPubkey, // randomnessAccountData
        rngPubkey  // randomnessAccount
      );

      const txid = await sendTx(connection, { publicKey, signTransaction }, [commitIx, playIx], [randomnessKp]);
      console.log("commit and play txid", txid)
    }

    try {
      await doPlay()
    } catch (err: any) {
      console.error(err);
      const retrySuccess = await new Promise<boolean>((resolve) =>
        setTimeout(() => {
          doPlay().then(() => resolve(true)).catch((e) => {
            console.error(e)
            resolve(false)
          })
        }, 2000))
      //alert("Play error: " + err.message);
      success = retrySuccess

      if (!success) {
        return false
      }
    }

    setTransactionStep('settle');
    const doSettle = async () => {
      console.log("here0", rngPubkey.toString())
      const randomness = new sb.Randomness(sbProgram, rngPubkey)
      console.log("here1", randomness.pubkey.toString())
      const revealIx = await randomness.revealIx();
      console.log("here2")

      const settleIx = createSettleInstruction(
        new PublicKey(ADMIN_PUBKEY),
        gameStatePda,
        playerState,
        rngPubkey,
        rewardPool,
        userMonopolyTokenAccount,
        gameStateAuthority
      );
      console.log("here3")

      const settleTxid = await sendTx(connection, { publicKey, signTransaction }, [revealIx, settleIx]);
      console.log("settle tx", settleTxid)
      success = true
    }

    try {
      await doSettle()
    } catch (err: any) {
      console.error(err);
      const retrySuccess = await new Promise<boolean>((resolve) =>
        setTimeout(() => {
          doSettle().then(() => resolve(true)).catch((e) => {
            console.error(e)
            resolve(false)
          })
        }, 3000))
      console.log('retrySuccess', retrySuccess)
      success = retrySuccess
      //alert("Reveal: " + err.message);
    }

    setTransactionStep(null);

    console.log('success', success)
    return success
  };

  // ------------------------- ADMIN INSTRUCTIONS -------------------------
  /** 
   * (UPDATED) handleInitGame uses the new `createInitGameInstruction` 
   * which requires gameStateAuthority, rollMint, and rewardMint as extra args
   */
  const handleInitGame = async () => {
    if (!publicKey || !signTransaction) return;
    try {
      const ix = createInitGameInstruction(
        publicKey,              // admin
        gameStatePda,
        gameStateAuthority,     // new arg
      );

      await sendTx(connection, { publicKey, signTransaction }, [ix]);
      alert("Game initialized successfully!");
    } catch (err: any) {
      console.error(err);
      alert("Error initializing game: " + err.message);
    }
  };

  const handleInitIncomeAccount = async () => {
    if (!publicKey || !signTransaction) return;
    try {
      const ix = createInitGameIncomeAccountInstruction(
        publicKey,              // admin
        gameStatePda,
        gameStateAuthority,     // new arg
        rollIncome,
        new PublicKey(ROLL_MINT),     // from constants
      );

      await sendTx(connection, { publicKey, signTransaction }, [ix]);
      alert("Game initialized successfully!");
    } catch (err: any) {
      console.error(err);
      alert("Error initializing game: " + err.message);
    }
  };

  const handleInitRewardAccount = async () => {
    if (!publicKey || !signTransaction) return;
    try {
      const ix = createInitGameRewardAccountInstruction(
        publicKey,              // admin
        gameStatePda,
        gameStateAuthority,     // new arg
        rewardPool,
        new PublicKey(MONOPOLY_MINT), // from constants
      );

      await sendTx(connection, { publicKey, signTransaction }, [ix]);
      alert("Game initialized successfully!");
    } catch (err: any) {
      console.error(err);
      alert("Error initializing game: " + err.message);
    }
  };

  const handleSetPayoutMap = async (payoutMap: PayoutConfig[]) => {
    if (!publicKey || !signTransaction) return;
    try {
      const ix = createSetPayoutMapInstruction(gameStatePda, publicKey, payoutMap);
      await sendTx(connection, { publicKey, signTransaction }, [ix]);
      alert("Payout map updated!");

      const gameStateParsed = await fetchGameState(connection, gameStatePda)
      setGameState(gameStateParsed)
    } catch (err: any) {
      console.error(err);
      alert("Error setting payout map: " + err.message);
    }
  };

  const handleWithdrawFromRewardPool = async () => {
    try {
      if (!publicKey || !signTransaction) return;

      // scale up user’s input
      const rawAmount = scaleUp(withdrawAmountMonopoly, MONOPOLY_DECIMALS);

      // Program-defined instruction
      const ix = createWithdrawFromRewardPoolInstruction(
        gameStatePda,
        publicKey,
        gameStateAuthority,
        rewardPool,
        userMonopolyTokenAccount, // destination
        rawAmount
      );

      await sendTx(connection, { publicKey, signTransaction }, [ix], []);
      alert(`Withdrew ${withdrawAmountMonopoly} MONOPOLY from reward pool!`);

      // refresh
      await fetchAllBalances();
    } catch (err: any) {
      console.error(err);
      alert("Error: " + err.message);
    }
  };

  const handleWithdrawIncome = async () => {
    try {
      if (!publicKey || !signTransaction) return;

      const rawAmount = scaleUp(withdrawAmountRoll, ROLL_DECIMALS);
      const ix = createWithdrawIncomeInstruction(
        gameStatePda,
        publicKey,
        gameStateAuthority,
        rollIncome,
        userRollTokenAccount,
        rawAmount
      );

      await sendTx(connection, { publicKey, signTransaction }, [ix], []);
      alert(`Withdrew ${withdrawAmountRoll} ROLL from income!`);

      // refresh
      await fetchAllBalances();
    } catch (err: any) {
      console.error(err);
      alert("Error: " + err.message);
    }
  };

  async function fetchGameState(
    connection: Connection,
    gameStatePubkey: PublicKey
  ): Promise<ParsedGameState | null> {
    // 1) Load raw account info
    const accountInfo = await connection.getAccountInfo(gameStatePubkey);
    if (!accountInfo) {
      // The account doesn't exist or isn't initialized
      return null;
    }

    // 2) Parse the data
    const gameState = parseGameStateData(accountInfo.data);

    return gameState;
  }

  useEffect(() => {
    (async () => {
      if (gameStatePda) {
        const gameStateParsed = await fetchGameState(connection, gameStatePda)
        console.log(gameStateParsed)
        setGameState(gameStateParsed)
      }
    })()
  }, [gameStatePda.toString()])

  // Load saved randomness keypair from localStorage
  useEffect(() => {
    if (!publicKey) return;

    const savedKp = localStorage.getItem(`rtd-randomness-${publicKey.toString()}`);
    if (savedKp) {
      try {
        const keypairBytes = new Uint8Array(JSON.parse(savedKp));
        const loadedKp = Keypair.fromSecretKey(keypairBytes);

        setRandomnessKp(loadedKp);
        setRandomnessPubkey(loadedKp.publicKey.toBase58());
        setIsInitialized(true);
      } catch (err) {
        console.error('Error loading saved keypair:', err);
      }
    } else {
      setIsInitialized(true);
    }
  }, [publicKey]);

  const handleDepositMonopoly = async () => {
    try {
      if (!publicKey || !signTransaction) {
        return alert("Wallet not connected.");
      }

      // Scale up from e.g. "1.5" => 1500000 raw units (for 6 decimals)
      const rawAmount = scaleUp(depositAmountMonopoly, MONOPOLY_DECIMALS);
      if (rawAmount <= 0) {
        return alert("Deposit amount must be greater than 0.");
      }

      // Build SPL Token transfer instruction
      const tx =
        createTransferCheckedInstruction(
          userMonopolyTokenAccount,     // source (admin's ATA)
          new PublicKey(MONOPOLY_MINT), // mint
          rewardPool,                   // destination (PDA)
          publicKey,                    // authority
          Number(rawAmount),            // must be number
          MONOPOLY_DECIMALS             // decimals
        )

      const txSig = await sendTx(connection, { publicKey, signTransaction }, [tx], []);
      console.log("Deposit txSig", txSig);
      alert(`Deposited ${depositAmountMonopoly} MONOPOLY into reward pool!`);

      // Refresh balances
      await fetchAllBalances();
    } catch (err: any) {
      console.error(err);
      alert("Error depositing: " + err.message);
    }
  };


  if (!publicKey) {
    return (
      <GameAlert title="Welcome to Roll The Dice!">
        <div className="space-y-4">
          <p>Ready to test your luck? Connect your wallet to start playing!</p>
          <div className="flex items-center gap-2 text-primary-400">
            <Dice className="w-6 h-6" />
            <span className="font-semibold">Burn $ROLL tokens for a chance to win $MONOPOLY rewards</span>
          </div>
        </div>
      </GameAlert>
    );
  }

  // ------------------------------------------------------------------
  // RENDER
  // ------------------------------------------------------------------
  return (
    <div className="pb-16">
      {isInitialized ? (
        <RollTheDiceGame
          payoutMap={initialPayoutMap || []}
          disabled={!sbProgram || !queuePubkey}
          onInitPlayer={async () => {
            return await handleInitPlayerAndRandomness()
          }}
          onInitRandom={async () => {
            return await handleInitRandomness()
          }}
          onPlay={async () => {
            const success = await handlePlay()
            fetchAllBalances()

            return success
          }}
          randomnessKp={randomnessKp}
          rewardPoolBalance={rewardPoolBalance}
          userRollBalance={userRollBalance}
          transactionStep={transactionStep}
          userMonopolyTokenAccount={userMonopolyTokenAccount}
          isLoadingQueue={!sbProgram || !queuePubkey}
        />
      ) : (
        <div>
        </div>
      )}

      {/* ADMIN PANEL */}
      {isAdmin && (
        <div className="bg-gray-800 p-4 rounded mt-4">
          <h2 className="text-2xl font-bold mb-4 text-yellow-300">Admin Panel</h2>

          {/* Init Game */}
          <div className="mb-4">
            <p className="text-sm text-gray-400 mb-2">
              Initialize the global game, creating PDAs & setting initial payout
            </p>
            <button
              className="px-6 py-2 bg-pink-600 text-white rounded-xl hover:bg-pink-700 transition"
              onClick={handleInitGame}
            >
              Initialize Game
            </button>
            <button
              className="px-6 py-2 bg-pink-600 text-white rounded-xl hover:bg-pink-700 transition"
              onClick={handleInitIncomeAccount}
            >
              Initialize Roll Income Account
            </button>
            <button
              className="px-6 py-2 bg-pink-600 text-white rounded-xl hover:bg-pink-700 transition"
              onClick={handleInitRewardAccount}
            >
              Initialize Reward Account
            </button>
          </div>

          {/* Payout Map Editor */}
          {initialPayoutMap && (
            <PayoutMapEditor
              currentPayoutMap={initialPayoutMap}
              onSave={async (newPayoutMap) => {
                try {
                  await handleSetPayoutMap(newPayoutMap);
                  // Refresh game state after saving
                  const updatedGameState = await fetchGameState(connection, gameStatePda);
                  setGameState(updatedGameState);
                } catch (error) {
                  console.error('Error saving payout map:', error);
                  alert('Failed to save payout map. Please try again.');
                }
              }}
            />
          )}

          {/* ~~~~~~~~~~ SHOW BALANCES ~~~~~~~~~~ */}
          <div className="mb-4">
            <h3 className="font-semibold mb-2 text-gray-200">PDA Balances</h3>
            <p className="text-gray-300 text-sm mb-1">Reward Pool (MONOPOLY): {rewardPoolBalance}</p>
            <p className="text-gray-300 text-sm mb-1">Roll Income (ROLL): {rollIncomeBalance}</p>
            <hr className="my-2" />
            <h3 className="font-semibold mb-2 text-gray-200">Your Balances</h3>
            <p className="text-gray-300 text-sm mb-1">Your ROLL: {userRollBalance}</p>
            <p className="text-gray-300 text-sm mb-1">Your MONOPOLY: {userMonopolyBalance}</p>
          </div>

          {/* ~~~~~~~~~~ DEPOSIT MONOPOLY INTO REWARD POOL ~~~~~~~~~~ */}
          <div className="mb-4">
            <h3 className="font-semibold mb-2 text-gray-200">Deposit MONOPOLY into Reward Pool</h3>
            <div className="flex items-center gap-2">
              <input
                type="number"
                placeholder="Enter MONOPOLY to deposit"
                value={depositAmountMonopoly}
                onChange={(e) => setDepositAmountMonopoly(e.target.value)}
                className="w-40 p-2 bg-gray-700 text-white rounded"
              />
              <button
                className="px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                onClick={() => {
                  // MAX button => set deposit to user's entire MONOPOLY
                  setDepositAmountMonopoly(userMonopolyBalance);
                }}
              >
                MAX
              </button>
              <button
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                onClick={handleDepositMonopoly}
              >
                Deposit
              </button>
            </div>
          </div>

          {/* ~~~~~~~~~~ WITHDRAW EXAMPLES ~~~~~~~~~~ */}
          <div className="mb-4">
            <h3 className="font-semibold mb-2 text-gray-200">Withdraw from Reward Pool (MONOPOLY)</h3>
            <div className="flex items-center gap-2 mb-2">
              <input
                type="number"
                className="w-40 p-2 bg-gray-700 text-white rounded"
                placeholder="Amount"
                value={withdrawAmountMonopoly}
                onChange={(e) => setWithdrawAmountMonopoly(e.target.value)}
              />
              <button
                className="px-3 py-2 bg-gray-600 text-white rounded hover:bg-gray-500"
                onClick={() => setWithdrawAmountMonopoly(rewardPoolBalance)}
              >
                MAX
              </button>
              <button
                className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
                onClick={handleWithdrawFromRewardPool}
              >
                Withdraw
              </button>
            </div>
          </div>

          <div className="mb-4">
            <h3 className="font-semibold mb-2 text-gray-200">Withdraw from Roll Income (ROLL)</h3>
            <div className="flex items-center gap-2 mb-2">
              <input
                type="number"
                className="w-40 p-2 bg-gray-700 text-white rounded"
                placeholder="Amount"
                value={withdrawAmountRoll}
                onChange={(e) => setWithdrawAmountRoll(e.target.value)}
              />
              <button
                className="px-3 py-2 bg-gray-600 text-white rounded hover:bg-gray-500"
                onClick={() => setWithdrawAmountRoll(rollIncomeBalance)}
              >
                MAX
              </button>
              <button
                className="px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700"
                onClick={handleWithdrawIncome}
              >
                Withdraw
              </button>
            </div>
          </div>

        </div>
      )}
    </div>
  );
};

const RtdGamePage = () => {
  const { publicKey } = useWallet();

  const [gameState, setGameState] = useState<PublicKey | null>(null);
  const [playerState, setPlayerState] = useState<PublicKey | null>(null);
  const [rollIncome, setRollIncome] = useState<PublicKey | null>(null);
  const [rewardPool, setRewardPool] = useState<PublicKey | null>(null);
  const [gameStateAuthority, setGameStateAuthority] = useState<PublicKey | null>(null);
  const [userRollTokenAccount, setUserRollTokenAccount] = useState<PublicKey | null>(null);
  const [userMonopolyTokenAccount, setUserMonopolyTokenAccount] = useState<PublicKey | null>(null);

  useEffect(() => {
    const setup = async () => {
      const admin = new PublicKey(ADMIN_PUBKEY);

      // 1) Derive GameState PDA
      const [gameStatePda] = deriveGameStatePda(RTD_PROGRAM_ID, admin);
      setGameState(gameStatePda);

      // 2) Derive the "game_state_authority"
      //    Example: if IDL seeds = [ "game-state-authority2", gameState.key() ]
      //    But we don’t have that code snippet, so in your real code you’d do:
      //        const [authorityPda] = deriveGameStateAuthorityPda(RTD_PROGRAM_ID, gameStatePda);
      //    For now, let’s just assume the admin is the authority or something simple
      const [gameStateAuthority] = findGameStateAuthorityPda(RTD_PROGRAM_ID, gameStatePda)
      setGameStateAuthority(gameStateAuthority);

      // 3) If user is connected, derive their PlayerState & token accounts
      if (publicKey) {
        const [playerStatePda] = derivePlayerStatePda(RTD_PROGRAM_ID, publicKey);
        setPlayerState(playerStatePda);

        const userRoll = getAssociatedTokenAddressSync(new PublicKey(ROLL_MINT), publicKey);
        console.log("userRoll", userRoll.toString())
        setUserRollTokenAccount(userRoll);

        const userMonopoly = getAssociatedTokenAddressSync(new PublicKey(MONOPOLY_MINT), publicKey);
        setUserMonopolyTokenAccount(userMonopoly);
      }

      // 4) Derive roll_income & reward_pool
      //    Example: seeds = [ "roll-income", gameStatePda ]
      //             seeds = [ "reward-pool", gameStatePda ]
      const [rollIncomePda] = findRollIncomePda(RTD_PROGRAM_ID, gameStatePda);
      const [rewardPoolPda] = findRewardPoolPda(RTD_PROGRAM_ID, gameStatePda);
      setRollIncome(rollIncomePda);
      setRewardPool(rewardPoolPda);
    };

    setup();
  }, [publicKey]);

  if (!publicKey) {
  return <WelcomeScreen />
  }

  if (
    !gameState ||
    !rollIncome ||
    !rewardPool ||
    !gameStateAuthority ||
    !playerState ||
    !userRollTokenAccount ||
    !userMonopolyTokenAccount
  ) {
    return (
      <div className="flex justify-center items-center h-screen dark:bg-gray-900 dark:text-white">
        <p className="text-xl">Loading game data...</p>
      </div>
    );
  }

  return (
    <RtdGamePageContent
      gameState={gameState}
      playerState={playerState}
      rollIncome={rollIncome}
      rewardPool={rewardPool}
      gameStateAuthority={gameStateAuthority}
      userRollTokenAccount={userRollTokenAccount}
      userMonopolyTokenAccount={userMonopolyTokenAccount}
    />
  );
};

const RtdGameContainer = () => {
  const endpoint = CLUSTER_URL || 'http://127.0.0.1:8899';

  return (
    <ConnectionProvider endpoint={endpoint}>
      <WalletProvider wallets={[]} autoConnect>
        <WalletModalProvider>
          <div className="dark min-h-screen flex flex-col">
            <header className="sticky top-0 z-50 py-4 px-4 md:px-8 flex flex-col sm:flex-row justify-between items-center gap-4 bg-gray-800/95 backdrop-blur-sm border-b border-gray-700">
              <div className="text-center sm:text-left">
                <h1 className="text-lg md:text-xl font-bold text-gray-100">
                  Roll The Dice
                </h1>
                <p className="text-sm text-gray-400">
                  Burn <span className="text-accent-400">$ROLL</span> to win <span className="text-accent-400">$MONOPOLY</span>
                </p>
              </div>
              <WalletMultiButton className="w-full sm:w-auto px-6 py-2 bg-accent-500 hover:bg-accent-600 rounded-xl transition-colors" />
            </header>

            <main className="flex-1 container mx-auto py-6 md:py-8 max-w-6xl">
              <div className="">
                {/* Game Section */}
                <section className="">
                  <div className="relative">
                    {/* Background Gradient */}
                    <div className="absolute inset-0 bg-gradient-to-b from-primary-600/20 to-transparent rounded-xl -z-10" />

                    {/* Game Component */}
                    <div className="relative p-4 md:p-8">
                      <RtdGamePage />

                      {/* How to Play Section */}
                      <div className="space-y-4 p-6 bg-gray-800/50 rounded-xl border border-gray-700/50">
                        <h2 className="text-xl font-bold text-primary-400">How to Play</h2>
                        <div className="grid gap-4 md:grid-cols-3">
                          <div className="p-4 bg-gray-800 rounded-lg border border-gray-700">
                            <div className="text-accent-400 mb-2">1. Connect</div>
                            <p className="text-sm text-gray-300">Connect your wallet and make sure you have $ROLL tokens</p>
                          </div>
                          <div className="p-4 bg-gray-800 rounded-lg border border-gray-700">
                            <div className="text-accent-400 mb-2">2. Roll</div>
                            <p className="text-sm text-gray-300">Click 'Roll Dice' to burn $ROLL and try your luck</p>
                          </div>
                          <div className="p-4 bg-gray-800 rounded-lg border border-gray-700">
                            <div className="text-accent-400 mb-2">3. Win</div>
                            <p className="text-sm text-gray-300">Match winning combinations to earn $MONOPOLY rewards</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              </div>
            </main>

            <footer className="mt-auto py-6 px-4 text-center text-sm text-gray-400">
              <p>Good luck and have fun! 🎲</p>
            </footer>
          </div>
        </WalletModalProvider>
      </WalletProvider>
    </ConnectionProvider>
  );
};

export default RtdGameContainer;
